/**
 * The header's universal search (Ctrl/⌘ K): the company's clients, items, vendors, manufacturers,
 * stock locations and team, and Kasten's manufacturer and supplier directories, only the kinds the
 * member may read (the API decides), plus the pages of the navigation.
 * A combobox over a grouped listbox: arrows move, Enter opens, Escape closes (APG combobox pattern).
 */
import { ArrowRight, Search } from "lucide-react";
import { Dialog as D } from "radix-ui";
import { useEffect, useId, useMemo, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";

import { useUniversalSearch, type SearchGroup } from "@/api/queries/search";
import { Badge, InlineError, Spinner } from "@/components/ui";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { cx } from "@/lib/cx";

import type { NavSection } from "./nav";

type Entry = { id: string; group: string; title: string; subtitle?: string; badges?: string[]; to: string };

/** Where a result stands: archived, linked to the K-SHOP directory, or a directory entry the company hasn't added. */
function badges(key: SearchGroup["key"], r: SearchGroup["results"][number]): string[] {
  const out: string[] = [];
  if (r.archived) out.push(key === "members" ? "Inactive" : "Archived");
  if (r.in_directory) out.push("K-SHOP directory");
  if (key === "directory_manufacturers" || key === "directory_suppliers") out.push(r.record_id ? "In your list" : "Not in your list");
  return out;
}

const MIN_QUERY = 2;
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** Where a result opens; the API names the kind, the frontend owns the routes. */
function resultLink(base: string, key: SearchGroup["key"], r: SearchGroup["results"][number]): string {
  switch (key) {
    case "clients":
      return `${base}/clients/${r.id}`;
    case "items":
      return `${base}/catalog/items/${r.id}`;
    case "vendors":
      return `${base}/ordering/vendors/${r.id}`;
    case "locations":
      return `${base}/ordering/locations?q=${encodeURIComponent(r.code ?? r.title)}`;
    case "members":
      return `${base}/team/${r.id}`;
    case "manufacturers":
      return `${base}/catalog/manufacturers/${r.id}`;
    // A directory entry the company has opens its own record; one it hasn't opens the list filtered
    // to the name, where the entry has its Add button.
    case "directory_manufacturers":
      return r.record_id ? `${base}/catalog/manufacturers/${r.record_id}` : `${base}/catalog/manufacturers?q=${encodeURIComponent(r.title)}`;
    case "directory_suppliers":
      return r.record_id ? `${base}/ordering/vendors/${r.record_id}` : `${base}/catalog/vendors?q=${encodeURIComponent(r.title)}`;
  }
}

export function UniversalSearch({ slug, sections, compact = false }: { slug: string; sections: NavSection[]; compact?: boolean }) {
  const [open, setOpen] = useState(false);

  // Ctrl/⌘ K opens it from anywhere, also while typing elsewhere (a modifier combination, WCAG 2.1.4).
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      // Not over another open dialog (one that is closing, data-state="closed", doesn't count).
      if (!open && document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return;
      event.preventDefault();
      setOpen((o) => !o);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const shortcut = isMac ? "⌘K" : "Ctrl K";
  return (
    <D.Root open={open} onOpenChange={setOpen}>
      <D.Trigger asChild>
        {compact ? (
          <button
            type="button"
            aria-label="Search"
            data-tour="universal-search"
            className="flex size-8 items-center justify-center rounded-md text-fg-muted transition-colors transition-fast hover:bg-surface-hover hover:text-fg"
          >
            <Search aria-hidden className="size-[18px]" />
          </button>
        ) : (
          <button
            type="button"
            data-tour="universal-search"
            aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
            className="flex h-7 w-full max-w-md items-center gap-2 rounded-md border border-line bg-surface-sunken px-2 text-sm text-fg-subtle transition-colors transition-fast hover:border-line-strong hover:text-fg-muted"
          >
            <Search aria-hidden className="size-4 shrink-0" />
            <span className="flex-1 truncate text-left">Search clients, items, vendors…</span>
            <kbd className="rounded-sm border border-line px-1 font-sans text-xs text-fg-subtle">{shortcut}</kbd>
          </button>
        )}
      </D.Trigger>
      <D.Portal>
        <D.Overlay className="animate-fade-in fixed inset-0 z-40 bg-overlay" />
        {/* The dialog focuses its first control, the search box, when it opens. */}
        <D.Content
          aria-describedby={undefined}
          className="animate-pop-in fixed top-[12vh] left-1/2 z-50 flex max-h-[70vh] w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-line bg-surface-raised shadow-lg"
        >
          <D.Title className="sr-only">Search</D.Title>
          {open && <SearchPanel slug={slug} sections={sections} onDone={() => setOpen(false)} />}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

function SearchPanel({ slug, sections, onDone }: { slug: string; sections: NavSection[]; onDone: () => void }) {
  const navigate = useNavigate();
  const listId = useId();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const q = query.trim();
  const debounced = useDebouncedValue(q);
  const search = useUniversalSearch(slug, debounced);
  const base = `/t/${slug}`;

  const groups = useMemo(() => {
    const out: Array<{ key: string; label: string; entries: Entry[] }> = [];
    if (q) {
      const needle = q.toLowerCase();
      const seen = new Set<string>();
      const pages = sections
        .flatMap((s) => (s.planned ? [] : s.items.filter((i) => !i.planned).map((i) => ({ section: s.label, label: i.label, to: i.to }))))
        .filter((p) => `${p.section} ${p.label}`.toLowerCase().includes(needle) && !seen.has(p.to) && Boolean(seen.add(p.to)))
        .slice(0, 4);
      if (pages.length)
        out.push({
          key: "pages",
          label: "Go to",
          entries: pages.map((p) => ({ id: `page:${p.to}`, group: "pages", title: p.label, subtitle: p.section, to: p.to })),
        });
    }
    // Only results for what is typed now (not a previous search kept while the next one loads).
    if (q.length >= MIN_QUERY && debounced === q)
      for (const g of search.data ?? [])
        out.push({
          key: g.key,
          label: g.label,
          entries: g.results.map((r) => ({
            id: `${g.key}:${r.id}`,
            group: g.key,
            title: r.title,
            subtitle: r.subtitle,
            badges: badges(g.key, r),
            to: resultLink(base, g.key, r),
          })),
        });
    return out;
  }, [q, debounced, sections, search.data, base]);

  const entries = groups.flatMap((g) => g.entries);
  const current = entries[Math.min(active, entries.length - 1)];
  const optionId = (entry: Entry) => `${listId}-${entry.id}`;
  const go = (entry: Entry | undefined) => {
    if (!entry) return;
    onDone();
    navigate(entry.to);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!entries.length) return;
    const index = Math.min(active, entries.length - 1);
    const next =
      event.key === "ArrowDown" ? (index + 1) % entries.length
      : event.key === "ArrowUp" ? (index - 1 + entries.length) % entries.length
      : null;
    if (next !== null) {
      event.preventDefault();
      setActive(next);
      document.getElementById(optionId(entries[next]))?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Enter") {
      event.preventDefault();
      go(current);
    }
  };

  const typing = q.length >= MIN_QUERY && debounced !== q;
  const settled = q.length >= MIN_QUERY && !typing && !search.isFetching;
  return (
    <>
      {/* The row shows focus (an accent underline); the global ring on the input would be clipped by the dialog. */}
      <div className="flex items-center gap-2 border-b border-line px-3 has-[input:focus-visible]:shadow-[inset_0_-2px_0_var(--color-accent)]">
        <Search aria-hidden className="size-4 shrink-0 text-fg-subtle" />
        <input
          role="combobox"
          aria-label="Search"
          aria-expanded={entries.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={current ? optionId(current) : undefined}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          placeholder="Search clients, items, vendors, locations, people and pages"
          className="h-11 min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle focus-visible:shadow-none"
        />
        {(typing || search.isFetching) && <Spinner label="Searching" />}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
        {search.isError && <InlineError error={search.error} />}
        <div id={listId} role="listbox" aria-label="Results">
          {groups.map((g) => (
            <div key={g.key} role="group" aria-labelledby={`${listId}-${g.key}`} className="pb-1">
              <div id={`${listId}-${g.key}`} className="px-2 pt-1.5 pb-1 text-xs font-medium text-fg-subtle">
                {g.label}
              </div>
              {g.entries.map((entry) => {
                const selected = entry === current;
                return (
                  // Options are never focused: the input handles the keyboard (aria-activedescendant).
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
                  <div
                    key={entry.id}
                    id={optionId(entry)}
                    role="option"
                    aria-selected={selected}
                    onMouseDown={(e) => e.preventDefault()} // keep focus in the input
                    onMouseMove={() => setActive(entries.indexOf(entry))}
                    onClick={() => go(entry)}
                    className={cx("flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm", selected && "bg-surface-hover")}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-fg">{entry.title}</span>
                      {entry.subtitle && <span className="block truncate text-xs text-fg-subtle">{entry.subtitle}</span>}
                    </span>
                    {entry.badges?.map((label) => (
                      <Badge key={label} dot={false}>
                        {label}
                      </Badge>
                    ))}
                    {selected && <ArrowRight aria-hidden className="size-4 shrink-0 text-fg-subtle" />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <p role="status" className={cx("px-2 py-6 text-center text-sm text-fg-muted", entries.length > 0 && "sr-only")}>
          {!q
            ? "Find clients, items, vendors, manufacturers, stock locations and people by name, SKU, account or code, and companies in the K-SHOP directory."
            : q.length < MIN_QUERY
              ? entries.length
                ? `${entries.length} pages`
                : "Keep typing…"
              : settled
                ? entries.length
                  ? `${entries.length} results`
                  : `Nothing matches “${q}”.`
                : ""}
        </p>
      </div>
    </>
  );
}
